<template>
  <div>
    <div class="d-flex justify-space-between">
      <div>
        <template v-if="showValidated">
          <p class="mb-3 subtitle-2 blue-grey--text text--darken-1 mt-n14">
            {{ $t('rev.validation.total_impact_validated') }}
          </p>
          <p class="h6 blue-grey--text text--darken-3 mb-8">
            {{ totalImpactText }}
          </p>
        </template>

        <p
          class="mb-3 subtitle-2 blue-grey--text text--darken-1"
          :class="{
            'mt-n15':
              hasRequestValidation || (!showValidated && isImpactValidated),
          }"
        >
          {{ $t('common.time_frame_impact') }}
        </p>
        <v-chip v-if="!editMode && !startDate && !endDate" small disabled>{{
          $t('common.in_progress_status')
        }}</v-chip>

        <v-tooltip top v-else>
          <template v-slot:activator="{ on }">
            <div
              v-on="on"
              class="d-flex align-end"
              data-tourid="reviewTab-input-impactsTimeFrame"
            >
              <DatePicker
                data-testid="review-start-date-picker"
                :allowed-dates="startDateAllowed"
                :editMode="editMode"
                :inputClass="'mr-4'"
                :label="$t('common.date.start_date')"
                :value="startDate"
                @change="handleStartDateChange"
              />
              <span
                v-if="!editMode"
                class="font-weight-400 blue-grey--text text--darken-3 font-size-16 mx-1"
              >
                {{ $t('common.to') }}
              </span>
              <DatePicker
                data-testid="review-end-date-picker"
                :allowed-dates="endDateAllowed"
                :editMode="editMode"
                :inputClass="'ml-4'"
                :label="$t('common.date.end_date')"
                :value="endDate"
                @change="handleEndDateChange"
              />
            </div>
          </template>

          {{
            $t('common.time.dayWithCount', {
              count: impactPeriodInDays,
            })
          }}
        </v-tooltip>
      </div>
    </div>
    <p class="mb-2 mt-6 subtitle-2 blue-grey--text text--darken-1">
      {{ $t('rev_cycle.review.impact') }}
    </p>

    <div class="d-flex flex-column align-start">
      <v-chip
        v-if="shouldShowChip"
        class="mb-2"
        small
        color="green lighten-2"
        data-testid="reviewValidation-chip-predictedFinancialImpact"
      >
        <v-icon color="green" size="14" class="mr-1">
          mdi-cash-multiple
        </v-icon>
        <span class="caption">
          {{ formattedValue }}
        </span>
        <PredictedFinancialImpactInfoTooltip
          data-testid="reviewValidation-chip-infoIcon"
          iconClass="ml-2"
          iconColor="green"
          top
          :iconSize="14"
          :text="$t('rev.financial_potential_impact.tooltip')"
        />
      </v-chip>

      <v-chip
        v-if="!editMode && !selectedImpactMetrics.length"
        small
        disabled
        data-testid="reviewValidation-chip-inProgress"
      >
        {{ $t('common.in_progress_status') }}
      </v-chip>
    </div>

    <Metrics
      v-if="editMode || selectedImpactMetrics.length"
      data-tourid="revImpacts-cards-impacts"
      testId="review-investment-metric-card"
      addButtonTourId="revImpacts-button-addImpact"
      isImpact
      :editMode="editMode"
      :initialMetrics="selectedImpactMetrics"
      :metrics="impactMetrics"
      :revCurrency="revCurrency"
      :organizationCurrency="organizationCurrency"
      :exchangeRate="exchangeRate"
      :showMetricsInTwoColumns="showMetricsInTwoColumns"
      @change="handleImpactsChange"
      @add="
        trackAddImpactMetric({
          view: 'review',
          revId: rev.id,
          revType: rev.type,
          metric: $event,
        })
      "
      @delete="
        trackDeleteImpactMetric({
          view: 'review',
          revId: rev.id,
          revType: rev.type,
          metric: $event,
        })
      "
    />

    <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
      {{ $t('rev_cycle.review.investment') }}
    </p>
    <p v-if="!editMode && !selectedInvestmentMetrics.length" class="ma-0 pa-0">
      {{ '-' }}
    </p>
    <Metrics
      v-else
      data-tourid="revImpacts-cards-investments"
      testId="review-investment-metric-card"
      addButtonTourId="revImpacts-button-addInvestment"
      :editMode="editMode"
      :initialMetrics="selectedInvestmentMetrics"
      :metrics="investmentMetrics"
      :revCurrency="revCurrency"
      :organizationCurrency="organizationCurrency"
      :exchangeRate="exchangeRate"
      :showMetricsInTwoColumns="showMetricsInTwoColumns"
      @change="handleInvestmentsChange"
      @add="
        trackAddInvestment({
          view: 'review',
          revId: rev.id,
          revType: rev.type,
          investment: $event,
        })
      "
      @delete="
        trackDeleteInvestment({
          view: 'review',
          revId: rev.id,
          revType: rev.type,
          investment: $event,
        })
      "
    />

    <p class="mb-4 mt-6 subtitle-2 blue-grey--text text--darken-1">
      {{ $t('common.other_one') }}
    </p>
    <TextQuestion
      data-private
      data-testid="rev-review-calculate"
      rows="6"
      :class="{ 'mt-4 mb-1': editMode, 'mt-4 mb-4': !editMode }"
      :tourId="'rev-textArea-calculate'"
      :value="estimationExplanation"
      :question="$t('rev_cycle.review.how_did_you_estimate_the_benefits')"
      :maxLength="$constants.rev.benefitEstimationMaxLength"
      :isEditMode="editMode"
      :isImpactValidated="isImpactValidated"
      @input="$emit('update', 'estimationExplanation', $event)"
      @focusOut="
        trackBenefitsEstimation({
          view: 'review',
          revId: rev.id,
          revType: rev.type,
          comment: estimationExplanation,
        })
      "
    />
    <CategoriesSelector
      data-private
      data-testid="rev-review-benefits"
      tourId="rev-button-categories"
      v-if="editMode"
      :label="$t('common.category_other')"
      :siteIds="[siteId]"
      v-model="selectedCategories"
    />
    <ChipsList
      v-else
      translationKey="common.categories"
      :items="selectedCategories"
      :title="$t('common.category_other')"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import currencies from '@/locales/currencies.json'
import mixin from '@/mixins/mixin.vue'
import revMixin from '@/mixins/revMixin.vue'
import moneyMixin from '@/mixins/moneyMixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import CategoriesSelector from '@/components/CategoriesSelector.vue'
import ChipsList from '@/components/ChipsList.vue'
import DatePicker from '@/components/Core/DatePicker.vue'
import Metrics from '@/components/Rev/Review/Metrics.vue'
import PredictedFinancialImpactInfoTooltip from '@/components/PredictedFinancialImpactInfoTooltip.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import constants from '@/constants'

const FILTERS = {
  ESTIMATED: 'Estimated',
  VALIDATED: 'Validated',
}

export default {
  name: 'ReviewValidation',

  mixins: [mixin, revMixin, trackerMixin, moneyMixin],

  components: {
    DatePicker,
    Metrics,
    PredictedFinancialImpactInfoTooltip,
    TextQuestion,
    CategoriesSelector,
    ChipsList,
  },

  props: {
    isImpactValidated: Boolean,
    rev: { type: Object, default: () => {} },
    editMode: Boolean,
    hasRequestValidation: Boolean,
    isRevImplemented: Boolean,
    showValidated: Boolean,
  },

  data() {
    return {
      impactMetrics: [],
      investmentMetrics: this.$constants.investments,
      startDate: this.rev?.impactStartsAt ?? null,
      endDate: this.rev?.impactEndsAt ?? null,
      showMetricsInTwoColumns: true,
    }
  },

  mounted() {
    this.getImpactMetrics()
  },

  computed: {
    ...mapGetters(['organizationId']),

    currencySymbol() {
      return _.get(currencies[this.revCurrency], 'symbol_native', '')
    },

    revCurrency() {
      return this.getRevCurrency(this.rev)
    },
    organizationCurrency() {
      return this.getOrganizationCurrency(this.rev)
    },
    exchangeRate() {
      return this.getExchangeRate(this.rev)
    },

    selectedImpactMetrics() {
      return (
        (this.showValidated
          ? this.rev?.validatedReview?.impacts
          : this.rev?.reviewPhase?.impacts) ?? []
      )
    },
    selectedInvestmentMetrics() {
      return (
        (this.showValidated
          ? this.rev?.validatedReview?.investments
          : this.rev?.reviewPhase?.investments) ?? []
      )
    },

    siteId() {
      return this.rev.location?.site?.id || this.rev.location?.site
    },

    estimationExplanation() {
      return this.showValidated
        ? this.rev?.validatedReview?.estimationExplanation ?? ''
        : this.rev?.reviewPhase?.estimationExplanation ??
            this.rev?.review?.estimationComment ??
            ''
    },

    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.$emit('update', 'categories', categories)
      },
    },

    totalImpactText() {
      const totalImpact = this.rev?.validatedReview?.totalImpact ?? null

      return totalImpact
        ? `
          ${this.currencySymbol}
          ${Number(totalImpact).toLocaleString(navigator.language)}
          ${this.revCurrency}
          `
        : '-'
    },

    impactPeriodInDays() {
      return utils.dates.calculateDifferenceInDays(this.startDate, this.endDate)
    },

    // Keep in sync with PredictedFinancialImpactAlert.showAlert - same threshold
    shouldShowChip() {
      return (
        !this.isFinanciallyValidated(this.rev) &&
        _.isNumber(this.rev?.predictedFinancialImpactInUSD) &&
        this.rev?.predictedFinancialImpactInUSD >=
          constants.rev.minSignificantPredictedFinancialImpact
      )
    },

    formattedValue() {
      return this.formatMoney(
        this.rev?.predictedFinancialImpactInUSD,
        this.revCurrency
      )
    },
  },

  methods: {
    async getImpactMetrics() {
      try {
        const { data } = await api.metrics.getRules(this.organizationId)

        const organizationMetrics = data.filter(
          metric => metric.isEnabled || !!metric.id
        )
        this.impactMetrics = organizationMetrics.map(organizationMetric => {
          return {
            ...organizationMetric.metric,
            isEnabled: organizationMetric.isEnabled,
          }
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    handleStartDateChange(date) {
      this.startDate = date
      this.$emit('update', 'startDate', utils.dates.getStartOfDay(date))
      this.trackChangeDate({
        view: 'review',
        revId: this.rev.id,
        revType: this.rev.type,
        date: { startDate: this.startDate, endDate: this.endDate },
      })
    },

    handleEndDateChange(date) {
      this.endDate = date
      this.$emit('update', 'endDate', utils.dates.getEndOfDay(date))
      this.trackChangeDate({
        view: 'review',
        revId: this.rev.id,
        revType: this.rev.type,
        date: { startDate: this.startDate, endDate: this.endDate },
      })
    },

    handleImpactsChange(impacts) {
      this.$emit('update', 'impactMetrics', impacts)
    },

    handleInvestmentsChange(investments) {
      this.$emit('update', 'investments', investments)
    },

    startDateAllowed(date) {
      if (!this.endDate) return true

      return utils.dates.calculateDifferenceInDays(date, this.endDate) >= 0
    },

    endDateAllowed(date) {
      if (!this.startDate) return true

      return utils.dates.calculateDifferenceInDays(this.startDate, date) >= 0
    },
  },

  watch: {
    showValidated() {
      this.startDate = this.rev?.impactStartsAt ?? null
      this.endDate = this.rev?.impactEndsAt ?? null
      this.trackClickReviewValidationFilter({
        type: this.showValidated ? FILTERS.VALIDATED : FILTERS.ESTIMATED,
      })
    },
  },
}
</script>
